import { createContext, useContext, useEffect, useLayoutEffect, useState, ReactNode } from 'react'
import api from '@/services/api'
import { brandRamp } from '@/utils/brandColor'

export interface PortalConfig {
  id: number
  company_name: string
  trading_name?: string
  tagline?: string
  subdomain: string
  logo_url?: string
  favicon_url?: string
  email_logo_url?: string
  hero_image_url?: string
  primary_color: string
  secondary_color?: string
  accent_color?: string
  custom_css?: string
  contact_phone?: string
  contact_email?: string
  contact_address?: string
  support_email?: string
  support_hours?: string
  social_facebook?: string
  social_twitter?: string
  social_instagram?: string
  social_linkedin?: string
  social_tiktok?: string
  terms_url?: string
  privacy_url?: string
  email_from_name?: string
  email_signature?: string
  footer_text?: string
  about_blurb?: string
  welcome_message?: string
  hero_title?: string
  hero_subtitle?: string
  // The portal's own Google reCAPTCHA site key. A key is bound to a domain list, so the
  // main-site key cannot render on *.mobileflow.uk — see useRecaptchaSiteKey().
  recaptcha_site_key?: string
  is_active: boolean
}

interface PortalContextValue {
  portal: PortalConfig | null
  isPortal: boolean
  isLoading: boolean
  subdomain: string | null
}

const PortalContext = createContext<PortalContextValue>({
  portal: null,
  isPortal: false,
  isLoading: false,
  subdomain: null,
})

export function usePortal() {
  return useContext(PortalContext)
}

const PORTAL_BASE_DOMAINS = ['mobileflow.uk', 'siluxmobile.co.uk']

function detectSubdomain(): string | null {
  // Preview override: ?portal=<slug> lets a portal be viewed on ANY host/port
  // (no subdomain DNS needed) — used for previewing white-label portals. Persisted
  // for the browser session so in-app navigation keeps the portal context.
  try {
    const preview = new URLSearchParams(window.location.search).get('portal')
    if (preview) { sessionStorage.setItem('portal_preview', preview); return preview }
    const stored = sessionStorage.getItem('portal_preview')
    if (stored) return stored
  } catch { /* sessionStorage unavailable — ignore */ }

  const host = window.location.hostname
  for (const base of PORTAL_BASE_DOMAINS) {
    if (host.endsWith(`.${base}`)) {
      const subdomain = host.slice(0, -(base.length + 1))
      if (subdomain && subdomain !== 'www') return subdomain
    }
  }
  return null
}

const BRAND_CACHE_KEY = 'portal_brand_primary'

/** Paint the palette from the last known brand colour for this portal. */
function applyPrimaryRamp(color: string) {
  const ramp = brandRamp(color)
  const root = document.documentElement
  for (const [step, triplet] of Object.entries(ramp)) {
    root.style.setProperty(`--color-primary-${step}`, triplet)
  }
}

export function PortalProvider({ children }: { children: ReactNode }) {
  const [portal, setPortal] = useState<PortalConfig | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const subdomain = detectSubdomain()

  // The palette defaults to Silux green, and the portal's real colour only arrives with
  // /portal/config — so a reseller's page would paint green for that first moment and then
  // snap to their brand. Re-apply the colour we saw last time BEFORE the first paint, so a
  // returning visitor never sees the flash. (A first-ever visit still resolves on load.)
  useLayoutEffect(() => {
    if (!subdomain) return
    try {
      const cached = localStorage.getItem(`${BRAND_CACHE_KEY}:${subdomain}`)
      if (cached) applyPrimaryRamp(cached)
    } catch { /* localStorage unavailable — the config fetch will still paint it */ }
  }, [subdomain])

  useEffect(() => {
    if (!subdomain) return
    setIsLoading(true)
    api.get(`/portal/config?subdomain=${subdomain}`)
      .then(r => setPortal(r.data))
      .catch(() => setPortal(null))
      .finally(() => setIsLoading(false))
  }, [subdomain])

  // Inject branding colours as CSS variables
  useEffect(() => {
    if (!portal) return
    const root = document.documentElement
    if (portal.primary_color) root.style.setProperty('--portal-primary', portal.primary_color)
    if (portal.secondary_color) root.style.setProperty('--portal-secondary', portal.secondary_color)
    if (portal.accent_color) root.style.setProperty('--portal-accent', portal.accent_color)

    // Repaint the WHOLE app in the reseller's colour by overriding the primary palette the
    // Tailwind classes are compiled against (see tailwind.config.js / index.css). Without
    // this only the handful of components written with inline styles were branded, and a
    // customer who clicked through to the shop, checkout or coverage checker was suddenly
    // looking at Silux green on the reseller's own domain.
    if (portal.primary_color) {
      applyPrimaryRamp(portal.primary_color)
      // Remembered so the next page load can paint the brand before the config arrives.
      try {
        localStorage.setItem(`${BRAND_CACHE_KEY}:${portal.subdomain}`, portal.primary_color)
      } catch { /* not fatal — only costs the pre-paint on the next visit */ }
    }
  }, [portal?.primary_color, portal?.secondary_color, portal?.accent_color])

  // Inject favicon dynamically
  useEffect(() => {
    if (!portal?.favicon_url) return
    let link: HTMLLinkElement | null = document.querySelector("link[rel='icon']")
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.head.appendChild(link)
    }
    link.href = portal.favicon_url
  }, [portal?.favicon_url])

  // Inject portal title
  useEffect(() => {
    if (portal?.company_name) {
      document.title = portal.company_name
    }
  }, [portal?.company_name])

  // Inject custom CSS into a dedicated <style> tag
  useEffect(() => {
    const STYLE_ID = 'portal-custom-css'
    let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null
    if (!portal?.custom_css) {
      if (style) style.remove()
      return
    }
    if (!style) {
      style = document.createElement('style')
      style.id = STYLE_ID
      document.head.appendChild(style)
    }
    style.textContent = portal.custom_css
  }, [portal?.custom_css])

  return (
    <PortalContext.Provider value={{ portal, isPortal: !!subdomain, isLoading, subdomain }}>
      {children}
    </PortalContext.Provider>
  )
}
